/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { logoutRequestAction } from '../../store/account/userActions';
import { createBrowserHistory } from 'history';
import './MultiSignalsNavbar.scss';
import multisignalsLogo from '../../assets/img/logo/logo.png';
import { Popover } from 'react-tiny-popover';
import { Col, Row } from 'react-bootstrap';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

// Import modern icons
import { 
  Home,
  ShoppingBag,
  Key,
  Settings,
  User,
  ChevronDown,
  ChevronUp,
  LogOut,
  FileText,
  HelpCircle,
  ExternalLink,
  Menu,
  X,
  Bot
} from 'lucide-react';

function Navigation({ ...props }) {
  const {
    user,
    logoutRequest,
    history,
    MainLayoutStatus,
    isPublic,
  } = props;
  
  const pathname = history?.location.pathname;
  const pathnameHash = history?.location.hash;
  const [profileOpen, setProfileOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { utils } = require('ethers');
  const { pathname: activePath } = useLocation();

  const handleLogout = () => {
    logoutRequest();
    setProfileOpen(false);
  };

  const pathIsActive = (path) => {
    const history = createBrowserHistory();
    const pathname = history.location.pathname.split('/')[1];
    return pathname?.toLowerCase() === path.toLowerCase();
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  function controlPath(cPath) {
    return activePath.startsWith(cPath);
  }

  // Modern Navigation Items
  const navigationItems = [
    {
      name: 'Home',
      path: '/home',
      icon: Home,
      isActive: activePath === '/home',
      requiresAuth: true
    },
    {
      name: 'Marketplace',
      path: '/marketplace',
      icon: ShoppingBag,
      isActive: activePath === '/marketplace',
      requiresAuth: true
    },
    {
      name: 'My Bots',
      path: '/my-bots',
      icon: Bot,
      isActive: activePath === '/my-bots',
      requiresAuth: true
    },
    {
      name: 'API Settings',
      path: '/general-settings/api-settings',
      icon: Key,
      isActive: controlPath('/general-settings/api-settings'),
      requiresAuth: true
    },
    {
      name: 'Bot Management',
      path: '/general-settings/bot-management',
      icon: Settings,
      isActive: controlPath('/general-settings/bot-management'),
      requiresAuth: true
    }
  ];

  // Modern Profile Dropdown
  const ModernProfileDropdown = () => (
    <div className="modern-profile-dropdown">
      <div className="profile-header">
        <div className="profile-avatar">
          <User size={20} />
        </div>
        <div className="profile-info">
          <div className="profile-name">
            {user?.full_name?.length > 15
              ? `${user.full_name.slice(0, 15)}...`
              : user.full_name}
          </div>
          <div className="profile-email">
            {user?.email?.length > 20
              ? `${user.email.slice(0, 20)}...`
              : user.email}
          </div>
        </div>
      </div>
      <div className="profile-divider"></div>
      <div className="profile-menu">
        <Link className="profile-menu-item" to="/profile" onClick={() => setProfileOpen(false)}>
          <User size={16} />
          <span className='text-white'>Profile Settings</span>
        </Link>
        <div className="profile-menu-item" onClick={handleLogout}>
          <LogOut size={16} />
          <span className='text-white'>Sign Out</span>
        </div>
      </div>
    </div>
  );

  // Modern About Dropdown
  const ModernAboutDropdown = () => (
    <div className="modern-about-dropdown">
      <Row className="g-3">
        {user && (
          <Col md={6}>
            <Link
              className="about-menu-item"
              to="/about"
              onClick={() => setAboutOpen(false)}
            >
              <div className="about-item-icon">
                <FileText size={20} />
              </div>
              <div className="about-item-content">
                <div className="about-item-title">About MultiSignals</div>
                <div className="about-item-description">
                  Learn more about our platform and features
                </div>
              </div>
            </Link>
          </Col>
        )}
        <Col md={user ? 6 : 12}>
          <div
            className="about-menu-item"
            onClick={() => {
              window.open('https://docs.multisignals.com', '_blank');
              setAboutOpen(false);
            }}
          >
            <div className="about-item-icon">
              <HelpCircle size={20} />
            </div>
            <div className="about-item-content">
              <div className="about-item-title">
                User Guide
                <ExternalLink size={14} className="external-icon" />
              </div>
              <div className="about-item-description">
                Complete documentation and tutorials
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );

  // Modern Navigation Link
  const ModernNavLink = ({ item, className = '' }) => (
    <Nav.Link
      as={Link}
      to={item.path}
      className={`modern-nav-link ${item.isActive ? 'active' : ''} ${className}`}
      onClick={() => setMobileMenuOpen(false)}
    >
      <item.icon size={18} />
      <span>{item.name}</span>
      {item.isActive && <div className="active-indicator"></div>}
    </Nav.Link>
  );

  // Mobile Menu Toggle
  const MobileMenuToggle = () => (
    <button
      className="mobile-menu-toggle"
      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      aria-label="Toggle navigation"
    >
      {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
    </button>
  );

  return (
    <div className="modern-navbar-container">
      <Navbar className="modern-navbar" expand="lg" fixed="top">
        <Container>
          {/* Brand Logo */}
          <Navbar.Brand as={Link} to={user ? "/home" : "/"} className="modern-brand">
            <img src={multisignalsLogo} alt="MultiSignals" className="brand-logo" />
            <span className="brand-text">MultiSignals</span>
          </Navbar.Brand>

          {/* Desktop Navigation */}
          <div className="d-none d-lg-flex align-items-center ms-auto" style={{ gap: '24px' }}>
            {user && (
              <Nav className="main-navigation">
                {navigationItems.map((item, index) => (
                  item.requiresAuth && <ModernNavLink key={index} item={item} />
                ))}
              </Nav>
            )}

            {/* Right Side Navigation */}
            <Nav className="right-navigation">
              {/* About Dropdown */}
              <Popover
                isOpen={aboutOpen}
                positions={['bottom', 'left', 'right']}
                padding={10}
                onClickOutside={() => setAboutOpen(false)}
                content={<ModernAboutDropdown />}
                parentElement={
                  typeof window !== 'undefined' ? document.body : undefined
                }
                zIndex={1060}
              >
                <button
                  className={`modern-nav-button ${aboutOpen ? 'active' : ''}`}
                  onClick={() => setAboutOpen(!aboutOpen)}
                >
                  <HelpCircle size={18} />
                  <span>Help</span>
                  {aboutOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </Popover>

              {/* Authentication Buttons */}
              {!user ? (
                <div className="auth-buttons">
                  <Link to="/login" className="modern-auth-btn login-btn text-white" >
                    Sign In
                  </Link>
                  <Link to="/signup" className="modern-auth-btn signup-btn">
                    Get Started
                  </Link>
                </div>
              ) : (
                /* User Profile Dropdown */
                <Popover
                  isOpen={profileOpen}
                  padding={10}
                  positions={['bottom']}
                  content={<ModernProfileDropdown />}
                  onClickOutside={() => setProfileOpen(false)}
                >
                  <button
                    className={`modern-profile-button ${profileOpen ? 'active' : ''}`}
                    onClick={() => setProfileOpen(!profileOpen)}
                  >
                    <div className="profile-avatar">
                      <User size={20} />
                    </div>
                    <div className="profile-text">
                      <div className="profile-name">
                        {user?.full_name?.length > 12
                          ? `${user.full_name.slice(0, 12)}...`
                          : user.full_name}
                      </div>
                      <div className="profile-status">
                        <div className="status-dot"></div>
                        <span>Online</span>
                      </div>
                    </div>
                    <ChevronDown size={16} className={`chevron ${profileOpen ? 'rotated' : ''}`} />
                  </button>
                </Popover>
              )}
            </Nav>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="d-lg-none">
            <MobileMenuToggle />
          </div>
        </Container>

        {/* Mobile Navigation */}
        <div className={`mobile-navigation ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-nav-header">
            <div className="mobile-user-info">
              {user ? (
                <div className="mobile-user-profile">
                  <div className="mobile-profile-avatar">
                    <User size={24} />
                  </div>
                  <div className="mobile-profile-info">
                    <div className="mobile-profile-name">{user.full_name}</div>
                    <div className="mobile-profile-email">{user.email}</div>
                  </div>
                </div>
              ) : (
                <div className="mobile-auth-section">
                  <Link to="/login" className="mobile-auth-btn login" onClick={() => setMobileMenuOpen(false)}>
                    Sign In
                  </Link>
                  <Link to="/signup" className="mobile-auth-btn signup" onClick={() => setMobileMenuOpen(false)}>
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>

          <div className="mobile-nav-content">
            {user && (
              <div className="mobile-nav-section">
                <div className="mobile-nav-title">Navigation</div>
                {navigationItems.map((item, index) => (
                  item.requiresAuth && (
                    <ModernNavLink key={index} item={item} className="mobile-nav-item" />
                  )
                ))}
              </div>
            )}

            <div className="mobile-nav-section">
              <div className="mobile-nav-title">Help & Support</div>
              {user && (
                <Link
                  to="/about"
                  className="mobile-nav-item mobile-nav-link"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <FileText size={18} />
                  <span>About MultiSignals</span>
                </Link>
              )}
              <div
                className="mobile-nav-item mobile-nav-link"
                onClick={() => {
                  window.open('https://docs.multisignals.com', '_blank');
                  setMobileMenuOpen(false);
                }}
              >
                <HelpCircle size={18} />
                <span>User Guide</span>
                <ExternalLink size={14} className="external-icon" />
              </div>
            </div>

            {user && (
              <div className="mobile-nav-section">
                <div className="mobile-nav-title">Account</div>
                <Link
                  to="/profile"
                  className="mobile-nav-item mobile-nav-link"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User size={18} />
                  <span>Profile Settings</span>
                </Link>
                <div
                  className="mobile-nav-item mobile-nav-link logout-item"
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                >
                  <LogOut size={18} />
                  <span >Sign Out</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div 
            className="mobile-menu-overlay" 
            onClick={() => setMobileMenuOpen(false)}
          ></div>
        )}
      </Navbar>

      {/* Navbar Spacer */}
      <div className="navbar-spacer"></div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    user: state.userReducer.user,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    logoutRequest: (payload) => {
      dispatch(logoutRequestAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Navigation);